/**
 * ====================================================================
 * FIKO CAD LINEAR DIMENSION
 * ====================================================================
 *
 * İki nokta arası düz mesafe ölçüsü.
 * CATIA/IronCAD tarzında profesyonel dimension çizimi.
 *
 * Özellikler:
 * - Extension lines (uzatma çizgileri)
 * - Dimension line (ana ölçü çizgisi)
 * - Bi-directional arrows (çift yönlü oklar)
 * - Centered text label (ortadan yazı)
 * - Axis-lock desteği (Horizontal/Vertical)
 * - Kamera-bağımsız boyutlandırma
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoSahne } from '../../FikoSahne';
import { getCanvasResolution } from '../../FikoCanvasResolution';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    GeometryReference,
    LinearDimensionOptions,
    AxisLock,
    TextPlacement,
    ArrowStyle,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

/**
 * Linear Dimension - İki nokta arası mesafe ölçüsü
 */
export class LinearDimension extends Dimension {
    // Referanslar
    private refA: GeometryReference;
    private refB: GeometryReference;

    // Hesaplanmış noktalar
    private pointA: THREE.Vector3 = new THREE.Vector3();
    private pointB: THREE.Vector3 = new THREE.Vector3();
    private projectedA: THREE.Vector3 = new THREE.Vector3();
    private projectedB: THREE.Vector3 = new THREE.Vector3();

    // Çizim elemanları
    private mainLine: LineSegments2 | null = null;
    private extLine1: LineSegments2 | null = null;
    private extLine2: LineSegments2 | null = null;
    private arrow1: THREE.Mesh | THREE.Line | null = null;
    private arrow2: THREE.Mesh | THREE.Line | null = null;
    private label: THREE.Sprite | null = null;

    // Özel seçenekler
    private axisLock: AxisLock;

    // Sahne referansı
    private scene: FikoSahne;

    // 🎯 v2.0.1: Sabit offset yönü (kamera bağımsız sürükleme için)
    // İlk oluşturulduğunda hesaplanır ve değişmez
    private _cachedOffsetDirection: THREE.Vector3 | null = null;

    // 🎯 v2.0.2: Döndürme açısı (radyan cinsinden)
    // Dimension line'ı ölçüm ekseni etrafında döndürür
    private _rotationAngle: number = 0;

    // 🔴 SABİT BOYUT SİSTEMİ - FikoOlcumCizgisi ile aynı pattern
    sabitBoyut = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // 🚀 PERFORMANS: Smooth scale interpolation için
    private lastKameraUpdateTime: number = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS: number = 100;
    private targetScaleAdjustment: number = 1;
    private currentScaleAdjustment: number = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    // Performans için static buffer'lar
    private static readonly _tempVec1 = new THREE.Vector3();
    private static readonly _tempVec2 = new THREE.Vector3();
    private static readonly _tempVec3 = new THREE.Vector3();
    private static readonly _tempQuat = new THREE.Quaternion();

    // Shared geometry (memory optimization)
    private static _sharedArrowGeometry: THREE.ConeGeometry | null = null;

    constructor(
        refA: GeometryReference,
        refB: GeometryReference,
        type: DimensionType,
        options: LinearDimensionOptions,
        scene: FikoSahne
    ) {
        super(type, options);

        this.refA = refA;
        this.refB = refB;
        this.scene = scene;
        this.axisLock = options.axisLock || AxisLock.NONE;

        // Tip'e göre axis lock ayarla
        if (type === DimensionType.HORIZONTAL) {
            this.axisLock = AxisLock.X;
        } else if (type === DimensionType.VERTICAL) {
            this.axisLock = AxisLock.Y;
        }
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Noktaları hesapla
            this.calculatePoints();

            // Değeri hesapla
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.COINCIDENT_POINTS,
                        message: 'Noktalar çakışık veya mesafe çok küçük'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // 🎯 FikoOlcumCizgisi ile uyumlu: Sahneye hazırla (event listener bağla)
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Linear dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Dimension'ı güncelle (kamera değişikliğinde)
     * 🎯 FikoOlcumCizgisi ile uyumlu: Sadece yazı sabit boyut, oklar ve çizgiler normal
     */
    update(): void {
        // 🎯 FikoOlcumCizgisi ile uyumlu: Oklar sabit boyutta kalır (scale güncellenmez)
        // updateArrowScales() çağrılmıyor
        // Scale güncelleme event listener üzerinden yapılır (kameraYonDegisti)
    }

    /**
     * 🎯 v2.0.0: Offset değerini güncelle ve geometriyi yeniden oluştur
     * CATIA tarzı sürükleme için: Orijinal noktalar sabit kalır, sadece offset değişir
     *
     * 🎯 v2.0.5 FIX: Negatif offset da destekleniyor - her iki yöne sürüklenebilir
     *
     * @param newOffset Yeni offset değeri (geometriden dimension line'a mesafe, negatif olabilir)
     */
    setOffset(newOffset: number): void {
        // 🎯 v2.0.5 FIX: Sınırlama kaldırıldı - negatif offset da destekleniyor
        // Eski: Math.max(5, newOffset) - sadece pozitif yöne izin veriyordu

        // Options'ı güncelle
        this._options.offset = newOffset;

        // Geometriyi yeniden oluştur (pointA ve pointB sabit kalır)
        this.buildGeometry();

        // 🎯 v3.2.3: SADECE LABEL'ı scale et (FikoOlcumCizgisi pattern)
        if (this.sabitBoyut && this.label && this.label.userData.originalScale && this.scene?.anaGosterim?.aktifKamera) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
            const scaleAdjustment = factor * 0.02;

            // 🎯 v3.2.3: SADECE LABEL'ı scale et
            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * scaleAdjustment,
                originalScale.y * scaleAdjustment,
                1
            );

            this.currentScaleAdjustment = scaleAdjustment;
            this.targetScaleAdjustment = scaleAdjustment;
        }

        // 🎯 Sahneyi güncelle
        if (this.scene?.anaGosterim) {
            this.scene.anaGosterim.ciz();
        }
    }

    /**
     * 🎯 v2.0.0: Mevcut offset değerini al
     */
    getOffset(): number {
        return this._options.offset || DIMENSION_CONSTANTS.DEFAULT_OFFSET;
    }

    /**
     * 🎯 v2.0.0: Orijinal geometri noktalarını al (sürükleme için)
     */
    getOriginalPoints(): { pointA: THREE.Vector3; pointB: THREE.Vector3 } {
        return {
            pointA: this.pointA.clone(),
            pointB: this.pointB.clone()
        };
    }

    /**
     * 🎯 v2.0.0: Projeksiyon noktalarını al (dimension line üzerindeki noktalar)
     */
    getProjectedPoints(): { projectedA: THREE.Vector3; projectedB: THREE.Vector3 } {
        return {
            projectedA: this.projectedA.clone(),
            projectedB: this.projectedB.clone()
        };
    }

    /**
     * 🎯 v2.0.2: Dimension'ı döndür (radyan cinsinden)
     * Ölçüm ekseni (pointA → pointB) etrafında döndürür
     * CATIA tarzı: Ölçüm noktaları sabit kalır, sadece dimension line döner
     *
     * @param angle Döndürme açısı (radyan)
     */
    setRotation(angle: number): void {
        this._rotationAngle = angle;

        // Cache'i temizle - yeni yön hesaplanacak
        this._cachedOffsetDirection = null;

        // Geometriyi yeniden oluştur
        this.buildGeometry();

        // 🎯 v3.2.3: SADECE LABEL'ı scale et (FikoOlcumCizgisi pattern)
        if (this.sabitBoyut && this.label && this.label.userData.originalScale && this.scene?.anaGosterim?.aktifKamera) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
            const scaleAdjustment = factor * 0.02;

            // 🎯 v3.2.3: SADECE LABEL'ı scale et
            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * scaleAdjustment,
                originalScale.y * scaleAdjustment,
                1
            );

            this.currentScaleAdjustment = scaleAdjustment;
            this.targetScaleAdjustment = scaleAdjustment;
        }

        // Sahneyi güncelle
        if (this.scene?.anaGosterim) {
            this.scene.anaGosterim.ciz();
        }
    }

    /**
     * 🎯 v2.0.2: Mevcut döndürme açısını al (radyan)
     */
    getRotation(): number {
        return this._rotationAngle;
    }

    /**
     * 🎯 v2.0.2: Dimension'ı belirli bir açı kadar döndür (radyan)
     * Mevcut açıya eklenir
     *
     * @param deltaAngle Eklenecek açı (radyan)
     */
    rotate(deltaAngle: number): void {
        this.setRotation(this._rotationAngle + deltaAngle);
    }

    /**
     * 🎯 v2.0.2: Dimension'ı derece cinsinden döndür
     *
     * @param degrees Döndürme açısı (derece)
     */
    setRotationDegrees(degrees: number): void {
        this.setRotation(degrees * Math.PI / 180);
    }

    /**
     * 🎯 v2.0.2: Mevcut döndürme açısını derece olarak al
     */
    getRotationDegrees(): number {
        return this._rotationAngle * 180 / Math.PI;
    }

    /**
     * 🎯 v2.0.1: Offset yönünü al (sürükleme için)
     * Bu yön, dimension'ın hangi yöne sürükleneceğini belirler.
     * İLK oluşturulduğunda hesaplanır ve SABİT KALIR (kamera bağımsız).
     * 🎯 v2.0.11: Döndürme açısı da hesaba katılır
     *
     * @returns Normalize edilmiş offset yön vektörü (rotasyon uygulanmış)
     */
    override getOffsetDirection(): THREE.Vector3 {
        // 🎯 Cache'den döndür (kamera döndüğünde bile aynı yön)
        let baseDirection: THREE.Vector3;
        if (this._cachedOffsetDirection) {
            baseDirection = this._cachedOffsetDirection.clone();
        } else {
            // Fallback: Henüz cache yoksa hesapla
            baseDirection = this.calculateAndCacheOffsetDirection();
        }

        // 🎯 v2.0.11: Döndürme açısını uygula
        // Eğer döndürme varsa, offset yönünü de döndür
        if (this._rotationAngle !== 0) {
            // Dimension line yönü (döndürme ekseni)
            const lineDir = new THREE.Vector3()
                .subVectors(this.projectedB, this.projectedA)
                .normalize();

            // Quaternion ile döndür
            const quaternion = new THREE.Quaternion();
            quaternion.setFromAxisAngle(lineDir, this._rotationAngle);
            baseDirection.applyQuaternion(quaternion);
        }

        return baseDirection;
    }

    /**
     * 🎯 v2.0.1: Offset yönünü hesapla ve cache'le
     * Bu metod sadece ilk buildGeometry() çağrısında çalışır
     */
    private calculateAndCacheOffsetDirection(): THREE.Vector3 {
        // Dimension line yönünü bul
        const lineDir = new THREE.Vector3()
            .subVectors(this.projectedB, this.projectedA)
            .normalize();

        // Varsayılan yukarı yön
        const up = new THREE.Vector3(0, 1, 0);

        // Çizgiye dik yön hesapla
        const offsetDir = new THREE.Vector3().crossVectors(lineDir, up);

        // Eğer paralelse (dikey çizgi), X yönünü kullan
        if (offsetDir.lengthSq() < 0.0001) {
            offsetDir.crossVectors(lineDir, new THREE.Vector3(1, 0, 0));
        }

        offsetDir.normalize();

        // Her zaman yukarı doğru olsun (tutarlılık için)
        if (offsetDir.y < 0) {
            offsetDir.negate();
        }

        // Cache'le - artık değişmez
        this._cachedOffsetDirection = offsetDir.clone();

        return offsetDir;
    }

    /**
     * ✅ Sahneye hazırlama - FikoOlcumCizgisi ile aynı pattern
     * 🎯 v3.2.3: Sadece LABEL sabit boyutlu (FikoOlcumCizgisi ile aynı)
     */
    hazirla(): void {
        // console.log('[LinearDimension] hazirla() çağrıldı', {
        //     scene: !!this.scene,
        //     anaGosterim: !!this.scene?.anaGosterim,
        //     aktifKamera: !!this.scene?.anaGosterim?.aktifKamera,
        //     label: !!this.label,
        //     originalScale: !!this.label?.userData?.originalScale,
        //     sabitBoyut: this.sabitBoyut
        // });

        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            // console.log('[LinearDimension] hazirla() - scene/kamera yok, çıkılıyor');
            return;
        }

        try {
            // 🎯 v3.2.3: İlk scale'i hemen ayarla (SADECE LABEL için)
            if (this.sabitBoyut && this.label && this.label.userData.originalScale) {
                const kamera = this.scene.anaGosterim.aktifKamera as any;
                const zoom = kamera?.zoom ?? 1;
                const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
                const scaleAdjustment = factor * 0.02;

                // İlk scale'i LABEL'a uygula
                const originalScale = this.label.userData.originalScale as THREE.Vector3;
                this.label.scale.set(
                    originalScale.x * scaleAdjustment,
                    originalScale.y * scaleAdjustment,
                    1
                );
                this.currentScaleAdjustment = scaleAdjustment;
                this.targetScaleAdjustment = scaleAdjustment;
            }

            // 🔴 Kamera değiştiğinde - EVENT LİSTENER (FikoOlcumCizgisi pattern'i)
            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }

        } catch (e) {
            // Hata durumunda sessizce devam et
        }
    }

    /**
     * 🚀 PERFORMANS: Throttled kamera event handler
     * Her frame'de değil, 100ms'de bir güncelle (zoom sırasında donmayı önler)
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        // 🚀 FIX: RAF queue dolmasını önle - setTimeout ile simple throttle
        if (this.kameraUpdateTimeout !== null) {
            // Zaten bekleyen bir update var, skiple
            return;
        }

        // Throttle: Son güncellemeden beri yeterli süre geçti mi?
        if (now - this.lastKameraUpdateTime < LinearDimension.KAMERA_THROTTLE_MS) {
            // setTimeout ile throttle (RAF queue overflow yok!)
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, LinearDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        // Hemen çalıştır
        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * 🎯 v3.2.1: TÜM ÖLÇÜM SABİT BOYUTLU - yazı, çizgi, oklar hepsi zoomdan bağımsız
     * FikoOlcumCizgisi ve Kumpas ile AYNI davranış
     */
    private kameraYonDegisti(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }
        if (!this.sabitBoyut) {
            // console.log('[LinearDimension] kameraYonDegisti - sabitBoyut kapalı, çıkılıyor');
            return;
        }
        if (!this.label || !this.label.userData.originalScale) {
            // console.log('[LinearDimension] kameraYonDegisti - label veya originalScale yok');
            return;
        }

        const kamera = this.scene.anaGosterim.aktifKamera as any;
        const zoom = kamera?.zoom ?? 1;
        const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

        // 🎯 v3.2.3: Sadece LABEL için hedef scale'i güncelle (FikoOlcumCizgisi ile aynı)
        this.targetScaleAdjustment = factor * 0.02;
        // console.log('[LinearDimension] kameraYonDegisti - zoom:', zoom, 'factor:', factor, 'targetScale:', this.targetScaleAdjustment);

        // 🎯 v3.2.3: Smooth interpolation başlat
        this.startSmoothInterpolation();
    }

    /**
     * 🎯 v3.2.3: SADECE LABEL scale et - FikoOlcumCizgisi ile AYNI davranış
     * Grubu scale etmek POZİSYONLARI da etkiler - bu YANLIŞ!
     */
    private startSmoothInterpolation(): void {
        // Zaten çalışıyorsa, tekrar başlatma
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.sabitBoyut || !this.label || !this.label.userData.originalScale) {
                this.interpolationRAF = null;
                return;
            }

            // 🎯 SMOOTH: Linear interpolation (lerp)
            const lerpFactor = 0.2; // 0.2 = responsive ve smooth denge
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            // Convergence check - hedefe çok yakınsa dur (gereksiz RAF)
            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            // Interpolate
            this.currentScaleAdjustment += diff * lerpFactor;

            // 🎯 v3.2.3: SADECE LABEL'ı scale et (FikoOlcumCizgisi pattern)
            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );

            // Next frame
            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        // İlk frame'i başlat
        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * 🎯 v3.2.3: Dışarıdan sabit boyut güncellemesi için
     * Dialog'dan sabitBoyut değiştiğinde çağrılır
     */
    sabitBoyutGuncelle(): void {
        // console.log('[LinearDimension] sabitBoyutGuncelle çağrıldı, sabitBoyut:', this.sabitBoyut);
        if (this.sabitBoyut) {
            // Sabit boyut açık - kamera scale'i uygula
            // console.log('[LinearDimension] Sabit boyut AÇIK - kamera scale uygulanıyor');
            this.kameraYonDegisti();
        } else {
            // Sabit boyut kapalı - label'ı base scale'e döndür
            // console.log('[LinearDimension] Sabit boyut KAPALI - base scale uygulanıyor');
            if (this.label) {
                const canvas = this.label.material.map?.image as HTMLCanvasElement;
                if (canvas) {
                    this.label.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);
                    this.label.userData.originalScale = this.label.scale.clone();
                    // console.log('[LinearDimension] Label scale ayarlandı:', this.label.scale.toArray());
                }
            }
        }
    }

    /**
     * Validate
     */
    validate(): boolean {
        const distance = this.projectedA.distanceTo(this.projectedB);
        return distance >= DIMENSION_CONSTANTS.MIN_DISTANCE;
    }

    /**
     * Noktaları hesapla
     */
    private calculatePoints(): void {
        // Orijinal noktaları al
        this.pointA = this.getPointFromRef(this.refA);
        this.pointB = this.getPointFromRef(this.refB);

        // Axis lock'a göre projeksiyon
        this.projectedA.copy(this.pointA);
        this.projectedB.copy(this.pointB);

        switch (this.axisLock) {
            case AxisLock.X:
                // Horizontal - Y ve Z sabit
                this.projectedB.y = this.projectedA.y;
                this.projectedB.z = this.projectedA.z;
                break;
            case AxisLock.Y:
                // Vertical - X ve Z sabit
                this.projectedB.x = this.projectedA.x;
                this.projectedB.z = this.projectedA.z;
                break;
            case AxisLock.Z:
                // Depth - X ve Y sabit
                this.projectedB.x = this.projectedA.x;
                this.projectedB.y = this.projectedA.y;
                break;
        }
    }

    /**
     * Referanstan nokta al
     */
    private getPointFromRef(ref: GeometryReference): THREE.Vector3 {
        if (ref.point) {
            return ref.point.clone();
        }
        if (ref.center) {
            return ref.center.clone();
        }
        return new THREE.Vector3();
    }

    /**
     * Değeri hesapla
     */
    protected calculateValue(): number {
        return this.projectedA.distanceTo(this.projectedB);
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        // Temizle
        this.clearGeometry();

        const options = this._options;
        // 🎯 CATIA TARZI İLK YERLEŞİM: offset henüz set değilse (ilk oluşturma) ölçülen boya
        // orantılı bir standoff ver → ölçü geometrinin üstüne binmez, okunaklı durur.
        // Sürükleme sonrası options.offset dolu olduğundan bu yalnızca bir kez çalışır.
        if (options.offset == null) {
            options.offset = Math.max(DIMENSION_CONSTANTS.DEFAULT_OFFSET, (this._value || 0) * 0.12);
        }
        const offset = options.offset;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;

        // Yön vektörü
        const direction = LinearDimension._tempVec1
            .subVectors(this.projectedB, this.projectedA)
            .normalize();

        // 🎯 v2.0.1: Offset yönü - İLK ÇAĞRIDA cache'le, sonra sabit kullan
        // Kamera döndüğünde bile sürükleme yönü değişmez
        let offsetDir: THREE.Vector3;
        if (this._cachedOffsetDirection) {
            // Zaten cache'lenmiş - sabit yönü kullan
            offsetDir = this._cachedOffsetDirection;
        } else {
            // İlk kez - hesapla ve cache'le
            offsetDir = this.calculateOffsetDirection(direction);
            this._cachedOffsetDirection = offsetDir.clone();
        }

        // Offset noktaları
        const offsetA = LinearDimension._tempVec2
            .copy(this.projectedA)
            .addScaledVector(offsetDir, offset);

        const offsetB = LinearDimension._tempVec3
            .copy(this.projectedB)
            .addScaledVector(offsetDir, offset);

        // 1. Ana dimension line
        this.mainLine = this.createLine(
            [offsetA.x, offsetA.y, offsetA.z, offsetB.x, offsetB.y, offsetB.z],
            color,
            lineWidth
        );
        this.mainLine.name = 'DimensionLine';
        this.add(this.mainLine);

        // 2. Extension lines
        // 🎯 v2.0.0 FIX: Extension line'lar ORİJİNAL geometri noktalarından (pointA, pointB) başlamalı
        // Böylece ölçümün hangi noktalar arasında yapıldığı görünür olur (CATIA tarzı)
        //
        // CATIA Style Extension Lines:
        // - Orijinal geometri noktasından (pointA/B) başlar
        // - Dimension line üzerindeki karşılık gelen noktaya (offsetA/B) gider
        // - Gap: Geometriden biraz uzakta başlar (görsel netlik)
        // - Overshoot: Dimension line'dan biraz taşar
        if (options.showExtensionLines !== false) {
            const gap = options.extensionLineGap || 0.5;
            const overshoot = options.extensionLineOvershoot || 3;

            // 🎯 YÜZEYE DEĞEN UZATMA ÇİZGİSİ: Seçili yüzeyin sınır kutusu (bbox) verildiyse, uzatma
            // çizgisinin yüzey ucunu dim-line ucunun (offsetA/offsetB) o kutuya EN YAKIN noktası yap.
            // Düzleme değil KUTUYA kıstırıldığı için, KÜÇÜK yüzeylerde bile ayak yüzeyin ÜSTÜNDE kalır
            // (sonsuz düzlemde dışarı kaçmaz) → çizgi her durumda yüzeye değer, sürüklenince üstünde kayar.
            // Kutu yoksa ya da ayak ~aynı noktaysa (in-plane/yan offset) eski pointA/pointB'ye düş.
            const _bb1 = (options as any).yuzeyBBox1 as THREE.Box3 | undefined;
            const _bb2 = (options as any).yuzeyBBox2 as THREE.Box3 | undefined;
            let yuzeyUcA = this.pointA;
            let yuzeyUcB = this.pointB;
            if (_bb1) {
                const ayakA = _bb1.clampPoint(offsetA, new THREE.Vector3());
                if (ayakA.distanceTo(offsetA) > 0.5) yuzeyUcA = ayakA;
            }
            if (_bb2) {
                const ayakB = _bb2.clampPoint(offsetB, new THREE.Vector3());
                if (ayakB.distanceTo(offsetB) > 0.5) yuzeyUcB = ayakB;
            }

            // Extension line 1: yuzeyUcA → offsetA (dimension line üstü)
            // Yön: yuzeyUcA'dan offsetA'ya
            const extDir1 = new THREE.Vector3().subVectors(offsetA, yuzeyUcA).normalize();
            // Başlangıç: yuzeyUcA'dan gap kadar ileri (offsetA yönüne)
            const ext1Start = yuzeyUcA.clone().addScaledVector(extDir1, gap);
            // Bitiş: offsetA'dan overshoot kadar ileri (aynı yönde)
            const ext1End = offsetA.clone().addScaledVector(extDir1, overshoot);

            this.extLine1 = this.createLine(
                [ext1Start.x, ext1Start.y, ext1Start.z, ext1End.x, ext1End.y, ext1End.z],
                color,
                lineWidth * 0.8,
                0.9
            );
            this.extLine1.name = 'ExtensionLine1';
            this.add(this.extLine1);

            // Extension line 2: yuzeyUcB → offsetB (dimension line üstü)
            const extDir2 = new THREE.Vector3().subVectors(offsetB, yuzeyUcB).normalize();
            const ext2Start = yuzeyUcB.clone().addScaledVector(extDir2, gap);
            const ext2End = offsetB.clone().addScaledVector(extDir2, overshoot);

            this.extLine2 = this.createLine(
                [ext2Start.x, ext2Start.y, ext2Start.z, ext2End.x, ext2End.y, ext2End.z],
                color,
                lineWidth * 0.8,
                0.9
            );
            this.extLine2.name = 'ExtensionLine2';
            this.add(this.extLine2);
        }

        // 3. Oklar
        // 🎯 FikoOlcumCizgisi ile uyumlu: Oklar uç noktada, pivot tabanında
        // Arrow1: offsetA'da, direction yönünde (B'ye doğru bakar)
        // Arrow2: offsetB'de, -direction yönünde (A'ya doğru bakar)
        const arrowSize = options.arrowSize || DIMENSION_CONSTANTS.DEFAULT_ARROW_SIZE;

        // Pivot tabanında olduğu için pozisyon kaydırması gerekmiyor
        // Ok ucu direkt pozisyon noktasında olacak
        this.arrow1 = this.createArrow(offsetA.clone(), direction.clone(), color, arrowSize);
        this.arrow1.name = 'Arrow1';
        this.add(this.arrow1);

        this.arrow2 = this.createArrow(offsetB.clone(), direction.clone().negate(), color, arrowSize);
        this.arrow2.name = 'Arrow2';
        this.add(this.arrow2);

        // 4. Label
        const labelPos = new THREE.Vector3().lerpVectors(offsetA, offsetB, 0.5);
        labelPos.addScaledVector(offsetDir, options.textOffset || 2);

        this.label = this.createLabel(this.formatValue(this._value), labelPos);
        this.label.name = 'Label';
        this.add(this.label);
    }

    /**
     * Offset yönünü hesapla (kameraya göre akıllı + döndürme desteği)
     * 🎯 v2.0.2: Döndürme açısı da hesaba katılır
     */
    private calculateOffsetDirection(direction: THREE.Vector3): THREE.Vector3 {
        const offsetDir = new THREE.Vector3();

        if (this.scene?.anaGosterim?.aktifKamera) {
            const camera = this.scene.anaGosterim.aktifKamera;
            const cameraDir = new THREE.Vector3()
                .subVectors(
                    new THREE.Vector3().lerpVectors(this.projectedA, this.projectedB, 0.5),
                    camera.position
                )
                .normalize();

            // Çizgiye ve kameraya dik
            offsetDir.crossVectors(direction, cameraDir).normalize();

            // Çok küçükse (paralel durumu)
            if (offsetDir.lengthSq() < 0.01) {
                offsetDir.crossVectors(direction, camera.up).normalize();
            }
        } else {
            // Fallback: yukarı yön
            offsetDir.set(0, 1, 0);
            if (Math.abs(direction.dot(offsetDir)) > 0.9) {
                offsetDir.set(1, 0, 0);
            }
            offsetDir.crossVectors(direction, offsetDir).normalize();
        }

        // Her zaman yukarı doğru (ilk hesaplamada)
        if (offsetDir.y < 0) {
            offsetDir.negate();
        }

        // 🎯 v2.0.2: Döndürme açısını uygula
        // Ölçüm ekseni (direction) etrafında döndür
        if (this._rotationAngle !== 0) {
            const quaternion = new THREE.Quaternion();
            quaternion.setFromAxisAngle(direction, this._rotationAngle);
            offsetDir.applyQuaternion(quaternion);
        }

        return offsetDir;
    }

    /**
     * Çizgi oluştur
     */
    private createLine(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): LineSegments2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        // 🎯 KRITIK: Resolution ayarla - bu olmadan çizgiler görünmez!
        const _res = getCanvasResolution(this.scene?.anaGosterim?.sunucu);
        material.resolution.set(_res.width, _res.height);

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    // Shared geometries for different arrow styles
    private static _sharedOpenArrowGeometry: THREE.BufferGeometry | null = null;
    private static _sharedDotGeometry: THREE.BufferGeometry | null = null;
    private static _sharedTickGeometry: THREE.BufferGeometry | null = null;

    /**
     * Ok oluştur - ArrowStyle enum desteği
     */
    private createArrow(
        position: THREE.Vector3,
        direction: THREE.Vector3,
        color: number,
        size: number
    ): THREE.Mesh | THREE.Line {
        const arrowStyle = this._options.arrowStyle || ArrowStyle.FILLED;

        // NONE style - görünmez placeholder
        if (arrowStyle === ArrowStyle.NONE) {
            const empty = new THREE.Mesh();
            empty.visible = false;
            return empty;
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        let geometry: THREE.BufferGeometry;

        switch (arrowStyle) {
            case ArrowStyle.OPEN:
                // Açık ok - V şeklinde (CATIA tarzı geniş ok)
                if (!LinearDimension._sharedOpenArrowGeometry) {
                    const shape = new THREE.Shape();
                    shape.moveTo(-0.35, -0.5);
                    shape.lineTo(0, 0.5);
                    shape.lineTo(0.35, -0.5);
                    LinearDimension._sharedOpenArrowGeometry = new THREE.ShapeGeometry(shape);
                    LinearDimension._sharedOpenArrowGeometry.rotateX(Math.PI / 2);
                }
                geometry = LinearDimension._sharedOpenArrowGeometry;
                break;

            case ArrowStyle.DOT:
                // Nokta (görünür boyutta)
                if (!LinearDimension._sharedDotGeometry) {
                    LinearDimension._sharedDotGeometry = new THREE.SphereGeometry(0.25, 12, 12);
                }
                geometry = LinearDimension._sharedDotGeometry;
                break;

            case ArrowStyle.TICK:
                // Çizgi (architectural tick) - 45 derece çapraz çizgi, kalın
                if (!LinearDimension._sharedTickGeometry) {
                    const tickShape = new THREE.Shape();
                    tickShape.moveTo(-0.35, -0.35);
                    tickShape.lineTo(0.35, 0.35);
                    tickShape.lineTo(0.42, 0.28);
                    tickShape.lineTo(-0.28, -0.42);
                    tickShape.closePath();
                    LinearDimension._sharedTickGeometry = new THREE.ShapeGeometry(tickShape);
                    LinearDimension._sharedTickGeometry.rotateX(Math.PI / 2);
                }
                geometry = LinearDimension._sharedTickGeometry;
                break;

            case ArrowStyle.FILLED:
            default:
                // Dolu ok (CAD standardı) - Geniş ve uzun profesyonel ok
                // 🎯 FikoOlcumCizgisi ile uyumlu: Pivot tabanında, ok ucu pozisyon noktasında
                if (!LinearDimension._sharedArrowGeometry) {
                    LinearDimension._sharedArrowGeometry = new THREE.ConeGeometry(0.3, 0.8, 12);
                    // Pivot noktasını cone'nin tabanına taşı (ok ucu pozisyonda olacak)
                    LinearDimension._sharedArrowGeometry.translate(0, -0.4, 0); // height/2 = 0.8/2 = 0.4
                }
                geometry = LinearDimension._sharedArrowGeometry;
                break;
        }

        const arrow = new THREE.Mesh(geometry, material);
        arrow.position.copy(position);
        arrow.scale.setScalar(size);

        // Yönlendir - Y ekseni yukarı varsayılan (FikoOlcumCizgisi ile uyumlu)
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        arrow.quaternion.copy(quaternion);

        arrow.renderOrder = Infinity;
        arrow.frustumCulled = false;

        return arrow;
    }

    /**
     * 🎯 v3.2.0: Label oluştur - SİYAH ARKAPLAN + BEYAZ YAZI
     * Tolerance desteği ile
     */
    private createLabel(text: string, position: THREE.Vector3): THREE.Sprite {
        const options = this._options;
        const fontSize = options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;
        const textColor = options.textColor || '#ffffff'; // 🎯 BEYAZ yazı
        const backgroundColor = (options as any).backgroundColor || '#000000'; // 🎯 SİYAH arkaplan
        const fontFamily = options.fontFamily || 'Arial, sans-serif';
        const tolerance = options.tolerance;

        // Canvas oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Tolerans metni hazırla
        let toleranceText = '';
        let hasTolerance = false;
        if (tolerance) {
            hasTolerance = true;
            if (tolerance.symmetric && tolerance.upper !== undefined) {
                toleranceText = `±${tolerance.upper.toFixed(options.precision || 2)}`;
            } else if (tolerance.upper !== undefined || tolerance.lower !== undefined) {
                const upper = tolerance.upper !== undefined ? `+${tolerance.upper.toFixed(options.precision || 2)}` : '';
                const lower = tolerance.lower !== undefined ? `${tolerance.lower.toFixed(options.precision || 2)}` : '';
                toleranceText = `${upper}\n${lower}`;
            }
        }

        // Font ayarla ve ölç
        const mainFontSize = fontSize * 4;
        const tolFontSize = fontSize * 2.5;
        ctx.font = `bold ${mainFontSize}px ${fontFamily}`;
        const mainMetrics = ctx.measureText(text);
        const mainWidth = mainMetrics.width;

        // Tolerans genişliği
        let tolWidth = 0;
        let tolHeight = 0;
        if (hasTolerance) {
            ctx.font = `${tolFontSize}px ${fontFamily}`;
            if (tolerance?.symmetric) {
                tolWidth = ctx.measureText(toleranceText).width;
                tolHeight = tolFontSize;
            } else {
                const lines = toleranceText.split('\n');
                lines.forEach(line => {
                    tolWidth = Math.max(tolWidth, ctx.measureText(line).width);
                });
                tolHeight = tolFontSize * 2;
            }
        }

        // Canvas boyutu
        const paddingX = 12;
        const paddingY = 6;
        const totalWidth = mainWidth + (hasTolerance ? tolWidth + 8 : 0);
        const totalHeight = Math.max(mainFontSize, tolHeight);
        canvas.width = totalWidth + paddingX * 2;
        canvas.height = totalHeight + paddingY * 2;

        // 🎯 v3.2.0: SİYAH ARKAPLAN ÇİZ
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Ana metin çiz
        ctx.font = `bold ${mainFontSize}px ${fontFamily}`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        const mainX = paddingX;
        const mainY = canvas.height / 2;

        // 🎯 v3.2.0: BEYAZ YAZI (gölge yok, temiz görünüm)
        ctx.fillStyle = textColor;
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.fillText(text, mainX, mainY);

        // Tolerans çiz
        if (hasTolerance && toleranceText) {
            ctx.font = `${tolFontSize}px ${fontFamily}`;
            ctx.textAlign = 'left';
            const tolX = mainX + mainWidth + 8;

            if (tolerance?.symmetric) {
                ctx.textBaseline = 'middle';
                ctx.fillText(toleranceText, tolX, mainY);
            } else {
                const lines = toleranceText.split('\n');
                ctx.textBaseline = 'bottom';
                ctx.fillText(lines[0], tolX, mainY);

                if (lines[1]) {
                    ctx.textBaseline = 'top';
                    ctx.fillText(lines[1], tolX, mainY);
                }
            }
        }

        // Texture oluştur
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        // Sprite material
        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        // Sprite oluştur
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        // 🎯 Kompakt yazı scale'i - küçük ve okunabilir
        sprite.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        // 🎯 FikoOlcumCizgisi ile uyumlu: Original scale'i sakla (sabit boyut için)
        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);
        // 🎯 v3.2.0: Stil bilgilerini kaydet
        sprite.userData.textColor = textColor;
        sprite.userData.backgroundColor = backgroundColor;
        sprite.userData.backgroundOpacity = 1; // 🎯 v3.2.5: Dialog uyumluluğu için opacity ekle
        sprite.userData.fontSize = fontSize;

        return sprite;
    }

    /**
     * Ok ölçeklerini güncelle
     */
    private updateArrowScales(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        const camera = this.scene.anaGosterim.aktifKamera;
        const baseSize = this._options.arrowSize || DIMENSION_CONSTANTS.DEFAULT_ARROW_SIZE;

        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                const distance = camera.position.distanceTo(arrow.position);
                const scale = baseSize * (distance * 0.01);
                arrow.scale.setScalar(Math.max(scale, baseSize * 0.5));
            }
        });
    }

    /**
     * Görünümü güncelle (state değişikliğinde)
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        // Çizgilerin rengini güncelle
        [this.mainLine, this.extLine1, this.extLine2].forEach(line => {
            if (line) {
                (line.material as LineMaterial).color.setHex(color);
            }
        });

        // Okların rengini güncelle
        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                (arrow.material as THREE.MeshBasicMaterial).color.setHex(color);
            }
        });
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        // Çizgileri temizle
        [this.mainLine, this.extLine1, this.extLine2].forEach(line => {
            if (line) {
                this.remove(line);
                line.geometry.dispose();
                (line.material as LineMaterial).dispose();
            }
        });

        // Okları temizle
        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                this.remove(arrow);
                // Geometry shared, dispose etme
                (arrow.material as THREE.Material).dispose();
            }
        });

        // Label temizle
        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
        }

        this.mainLine = null;
        this.extLine1 = null;
        this.extLine2 = null;
        this.arrow1 = null;
        this.arrow2 = null;
        this.label = null;
    }

    /**
     * Dispose - 🎯 FikoOlcumCizgisi ile uyumlu kaynak temizleme
     */
    override dispose(): void {
        if (this.isDisposed) return; // Double dispose önleme
        this.isDisposed = true;

        // 🚀 Timeout temizle (memory leak önleme)
        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        // 🎯 SMOOTH: Interpolation RAF temizle
        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        // Event listener temizle
        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
